<template>
  <div class="record-page">
    <!-- Header -->
    <div class="viva-page-header">
      <div class="viva-header-content">
        <img src="/viva/viva_logo.svg" alt="Viva" class="viva-logo" />
        <h1 class="viva-page-title">START QUESTIONS</h1>
      </div>
      <ProfileDropdown
        :student-name="studentName"
        :is-assignment="isAssignment"
        :disable-navigation="questionsStarted"
      />
    </div>

    <!-- Pre-Recording Modal 1: Quiet Place -->
    <div v-if="currentPreRecordingModal === 1" class="viva-modal-backdrop" @click="handleModalBackdropClick">
      <div class="viva-modal-content" @click.stop>
        <div class="viva-modal-header">
          <h2>Readiness Check</h2>
          <div class="viva-modal-progress">1 of 4</div>
        </div>
        <div class="viva-modal-body">
          <div class="viva-requirement-item">
            <i class="fa fa-microphone viva-requirement-icon"></i>
            <div class="viva-requirement-content">
              <p>Are you in a quiet place where you can hear and speak freely? Type 'Yes' if so:</p>
              <div class="input-section">
                <input
                  data-cy="record-readyQuestion1-yes"
                  v-model="quietPlaceAnswer"
                  type="text"
                  class="form-control modal-input"
                  placeholder="Type 'Yes' here"
                  @keyup.enter="checkQuietPlace"
                >
              </div>
            </div>
          </div>
        </div>
        <div class="viva-modal-footer">
          <button
            data-cy="record-readyQuestion1-confirm"
            class="viva-btn viva-btn-green viva-btn-block"
            @click="checkQuietPlace"
            :disabled="!quietPlaceAnswer.trim()"
          >
            Continue
          </button>
        </div>
      </div>
    </div>

    <!-- Pre-Recording Modal 2: Time Check -->
    <div v-if="currentPreRecordingModal === 2" class="viva-modal-backdrop" @click="handleModalBackdropClick">
      <div class="viva-modal-content" @click.stop>
        <div class="viva-modal-header">
          <h2>Readiness Check</h2>
          <div class="viva-modal-progress">2 of 4</div>
        </div>
        <div class="viva-modal-body">
          <div class="viva-requirement-item">
            <i class="fa fa-clock-o viva-requirement-icon"></i>
            <div class="viva-requirement-content">
              <p>Do you have an uninterrupted 10 minutes to take this VIVA? Type 'Yes' if so:</p>
              <div class="input-section">
                <input
                  data-cy="record-readyQuestion2-yes"
                  v-model="timeAvailableAnswer"
                  type="text"
                  class="form-control modal-input"
                  placeholder="Type 'Yes' here"
                  @keyup.enter="checkTimeAvailable"
                >
              </div>
            </div>
          </div>
        </div>
        <div class="viva-modal-footer">
          <button
            data-cy="record-readyQuestion2-confirm"
            class="viva-btn viva-btn-green viva-btn-block"
            @click="checkTimeAvailable"
            :disabled="!timeAvailableAnswer.trim()"
          >
            Continue
          </button>
        </div>
      </div>
    </div>

    <!-- Pre-Recording Modal 3: Face Visible -->
    <div v-if="currentPreRecordingModal === 3" class="viva-modal-backdrop" @click="handleModalBackdropClick">
      <div class="viva-modal-content" @click.stop>
        <div class="viva-modal-header">
          <h2>Readiness Check</h2>
          <div class="viva-modal-progress">3 of 4</div>
        </div>
        <div class="viva-modal-body">
          <div class="viva-requirement-item">
            <div class="viva-requirement-content">
              <div class="video-container">
                <video 
                  autoplay 
                  muted 
                  playsinline
                  class="recording-video"
                  ref="videoElement"
                  id="testViewfinder"
                ></video>
              </div>
              <p>Is your whole face fully visible and clear on the screen? Type 'Yes' if so:</p>
              <div class="input-section">
                <input
                  data-cy="record-readyQuestion3-yes"
                  v-model="faceVisibleAnswer" 
                  type="text" 
                  class="form-control modal-input"
                  placeholder="Type 'Yes' here"
                  @keyup.enter="checkFaceVisible"
                >
              </div>
            </div>
          </div>
        </div>
        <div class="viva-modal-footer">
          <button
            data-cy="record-readyQuestion3-confirm"
            class="viva-btn viva-btn-green viva-btn-block" 
            @click="checkFaceVisible"
            :disabled="!faceVisibleAnswer.trim()"
          >
            Continue
          </button>
        </div>
      </div>
    </div>
    
     <!-- Pre-Recording Modal 4: Notes Check -->
    <div v-if="currentPreRecordingModal === 4" class="viva-modal-backdrop" @click="handleModalBackdropClick">
      <div class="viva-modal-content" @click.stop>
        <div class="viva-modal-header">
          <h2>Readiness Check</h2>
          <div class="viva-modal-progress">4 of 4</div>
        </div>
        <div class="viva-modal-body">
          <div class="viva-requirement-item">
            <i class="fa fa-pencil-square-o viva-requirement-icon"></i>
            <div class="viva-requirement-content">
              <p>I’ve confirmed that I’ve put away all materials, that the work I’ve uploaded is my own, and that I will not rely on any source other than what’s in my head when I’m answering questions. Type ‘Yes’ if so:</p>
              <div class="input-section">
                <input
                  data-cy="record-readyQuestion4-yes"
                  v-model="integrityAnswer"
                  type="text"
                  class="form-control modal-input"
                  placeholder="Type ‘Yes’ here"
                  @keyup.enter="checkIntegrity"
                >
              </div>
            </div>
          </div>
        </div>
        <div class="viva-modal-footer">
          <button
            data-cy="record-readyQuestion4-submit"
            class="viva-btn viva-btn-green viva-btn-block"
            @click="checkIntegrity"
            :disabled="!integrityAnswer.trim()"
          >
            Continue
          </button>
        </div>    
      </div>
    </div>

    <!-- Countdown Overlay -->
    <div v-if="showCountdown" data-cy="record-countdownOverlay" class="countdown-overlay">
      <div class="countdown-content">
        <h2>Get Ready!</h2>
        <div class="countdown-number">{{ countdownValue }}</div>
        <p>Recording will begin in {{ countdownValue }} second{{ countdownValue !== 1 ? 's' : '' }}</p>
      </div>
    </div>

    <!-- Main Recording Interface -->
    <div v-if="currentPreRecordingModal === 0" class="recording-interface">
      <div class="viva-container">
        <div class="recording-layout">
          <!-- Left Panel: Controls -->
          <div class="controls-panel">
            <!-- Start Button (only shows before first question) -->
            <div v-if="!questionsStarted && twilioJoined" class="start-section">
              <button
                data-cy="record-examQuestion-start"
                class="viva-btn viva-btn-green start-question-btn"
                @click="startFirstQuestion"
                :disabled="questionsLoading || audioGenerating"
              >
                <span v-if="questionsLoading">
                  <i class="fa fa-spinner fa-spin viva-spinner-small"></i> Loading Questions...
                </span>
                <!-- audioGenerating is mutually exclusive with questionsLoading: loadQuestionsFromAPI()
                     always sets questionsLoading false (in its finally block) before a 'generating_audio'
                     response can start this second spinner. -->
                <span v-else-if="audioGenerating">
                  <i class="fa fa-spinner fa-spin viva-spinner-small"></i> Preparing audio...
                </span>
                <span v-else>START QUESTION 1</span>
              </button>

              <!-- Error message display -->
              <div v-if="questionsError" class="questions-error">
                <p><i class="fa fa-exclamation-triangle"></i> {{ questionsError }}</p>
                <p>Please refresh the page and try again. If the problem persists, please contact support at <a class="link-accent" href="mailto:viva@initialview.com">viva@initialview.com</a>.</p>
              </div>
            </div>

            <!-- Video Take Not Ready Error (PDF still processing) -->
            <div v-if="!questionsStarted && videoTakeNotReady" class="questions-error">
              <p><i class="fa fa-exclamation-triangle"></i> Questions are not yet ready - check back in a few minutes</p>
              <p>If the problem persists, please email us at <a class="link-accent" href="mailto:viva@initialview.com">viva@initialview.com</a></p>
            </div>

            <!-- Twilio Backup Error -->
            <div v-if="!questionsStarted && twilioFailed" class="questions-error">
              <p><i class="fa fa-exclamation-triangle"></i> Unable to connect to backup recording service.</p>
              <p>Please refresh your page and try again. If the problem persists, contact support at <a class="link-accent" href="mailto:viva@initialview.com">viva@initialview.com</a></p>
            </div>
            
            <!-- Question Header -->
            <div v-if="questionsStarted" class="question-header">
              <h3 class="question-title">Question {{ currentQuestionIndex + 1 }} of {{ questions.length }}</h3>
            </div>

            <!-- Timer -->
            <div v-if="questionsStarted" class="timer-section">
              <div class="timer-box">
                <div class="timer-value">{{ timeLeft }}</div>
              </div>
                <div class="timer-label">Seconds</div>
            </div>

            <!-- Question Text (for deaf/hard-of-hearing accommodation) -->
            <div v-if="questionsStarted && textQuestionsEnabled && questions[currentQuestionIndex]" class="question-text-section">
              <div class="question-text-box">
                <p class="question-text-content">{{ questions[currentQuestionIndex].question }}</p>
                <p v-if="isSpeaking" class="question-text-note">
                  <i class="fa fa-volume-up"></i>
                  Interviewer is speaking...
                </p>
                <p v-else class="question-text-note question-text-ready">
                  <i class="fa fa-microphone"></i>
                  Please respond now
                </p>
              </div>
            </div>

            <!-- Waveform (shows during recording) -->
            <div v-if="questionsStarted" class="waveform-section">
              <div class="waveform-container">
                <div class="waveform-bars">
                  <div class="bar" v-for="n in 45" :key="n" :style="{ height: getBarHeight(n) }"></div>
                </div>
              </div>
            </div>

            <!-- Control Buttons -->
            <div v-if="questionsStarted" class="control-buttons">
              <button
                data-cy="record-examQuestion-repeat"
                class="viva-btn viva-btn-orange control-btn"
                @click="repeatQuestion"
                :disabled="isSpeaking"
              >
                <i class="fa fa-volume-up"></i>
                Repeat question (timer will pause)
              </button>
              
              <button
                data-cy="record-examQuestion-next"
                v-if="currentQuestionIndex < questions.length - 1"
                class="viva-btn viva-btn-orange control-btn" 
                @click="concludeRecording"
                :disabled="!isRecording || isSpeaking"
              >
                Conclude recording and go to next question
              </button>
              <button
                data-cy="record-examQuestion-submit"
                v-if="currentQuestionIndex === questions.length - 1"
                class="viva-btn viva-btn-orange control-btn" 
                @click="concludeRecording"
                :disabled="!isRecording || isSpeaking"
              >
                Conclude recording and submit
              </button>
            </div>

            <!-- Back Button -->
            <div v-if="!questionsStarted" class="back-section">
              <router-link to="/practice" class="back-link">
                <i class="fa fa-arrow-left"></i> Back
              </router-link>
            </div>
          </div>

          <!-- Right Panel: Video -->
          <div class="video-panel">
            <div class="video-container">
              <video 
                ref="videoElement" 
                id="viewfinder"
                autoplay 
                muted 
                playsinline
                class="recording-video"
              ></video>
              <div v-if="!cameraReady" class="camera-placeholder">
                <i class="fa fa-video-camera"></i>
                <p>Setting up camera...</p>
              </div>
              <!-- Recording Indicator -->
              <div v-if="isRecording" class="recording-indicator">
                <div class="recording-dot"></div>
                <span class="recording-text">REC</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Upload Progress Overlay -->
    <div v-if="showUploadProgress" class="upload-progress-overlay">
      <div class="upload-progress-content">
        <h3>Processing Your VIVA</h3>
        <div class="reassurance-header">
          <i class="fa fa-shield backup-shield-icon"></i>
          <h3>Please allow your recording to upload!</h3>
          <p class="backup-message">Please keep this tab open until your recording finishes uploading and processing. 
            If you experience any issues, you can contact support at <a class="link-accent" href="mailto:viva@initialview.com">viva@initialview.com</a>.</p>
        </div>

        <!-- Upload Error State -->
        <div v-if="uploadError" class="upload-error-state">
          <i class="fa fa-exclamation-triangle error-icon"></i>
          <h4>Upload Issue Detected</h4>
          <!-- <p class="error-message">{{ uploadError }}</p> -->
          <div class="error-reassurance">
            <p><strong>Don't worry!</strong> Your VIVA is safely backed up and will be reviewed normally.</p>
            <p>You can try uploading again, or simply continue - your VIVA has been successfully submitted.</p>
          </div>
          <div class="error-actions">
            <button class="viva-btn viva-btn-orange" @click="retryUpload">
              Try Upload Again
            </button>
            <button class="viva-btn viva-btn-secondary" @click="$router.push('/success')">
              Continue
            </button>
          </div>
        </div>

        <!-- Normal Upload States -->
        <div v-else-if="!uploadComplete">
          <!-- Upload Progress Bar -->
          <div v-if="originalVideoUploadProgress < 100" class="upload-progress">
            <!-- Backup Status Indicator -->
            <!-- <div class="backup-status">
              <i class="fa fa-check-circle backup-check"></i>
              <span>Backup recording secured ✓</span>
            </div> -->

            <div class="progress">
              <div
                class="progress-bar progress-bar-striped active"
                :class="{ 'progress-stalled': uploadStalled }"
                :style="{ width: originalVideoUploadProgress + '%' }"
              >
                {{ originalVideoUploadProgress }}%
              </div>
            </div>
            
            <div class="upload-status-text">
              <p v-if="!uploadStalled" class="text-center">Uploading your recording...</p>
            </div>

            <!-- Time elapsed -->
            <p v-if="uploadStalled" class="upload-time">Time elapsed: {{ formatUploadTime(uploadTimeoutSeconds) }}</p>

            <!-- Stalled instructions -->
            <div v-if="showRetryInstructions" class="stalled-instructions">
              <div class="stalled-help">
                <i class="fa fa-info-circle"></i>
                <div class="stalled-content">
                  <h5>Upload taking longer than expected?</h5>
                  <p>This can happen with large video files or slow internet connections.</p>
                  <ul>
                    <li>Wait a bit longer to see if the upload resumes.</li>
                    <li>If there's no progress, try restarting the upload.</li>
                    <li>If issues persist, contact us at <a class="link-accent" href="mailto:viva@initialview.com">viva@initialview.com</a></li>
                    <br />
                    <li>Don't panic! We have a backup of your recording just in case.</li>
                  </ul>
                </div>
              </div>
              <button data-cy="record-restartUpload" class="viva-btn viva-btn-orange retry-btn" @click="retryUpload">
                Restart Upload
              </button>
            </div>
          </div>

          <!-- Processing Phase (after upload completes) -->
          <div v-else>
            <div class="viva-spinner-section">
              <i class="fa fa-spinner fa-spin viva-spinner"></i>
              <p class="viva-spinner-text">Processing your recording...</p>
            </div>
          </div>
        </div>

        <!-- Complete -->
        <div v-else class="upload-complete">
          <i class="fa fa-check-circle success-icon"></i>
          <p>Uploading complete!</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ivRecorder, ivRecordingMixin } from "iv-record";
import { setEnv, ivChunkUploadMixin, ivDiagnosticsMixin } from "iv-base-components";
import { ivTwilioWebrtcMixin } from "../mixins/twilio-webrtc-mixin.js";
import ivbus from "iv-base-components/ivbus.js";
import env from "../utils/env.js";
import apiClient from "../utils/apiClient.js";
import { retryCompleteRecording } from "../utils/retryWithBackoff.js";
import ProfileDropdown from '../components/ProfileDropdown.vue';
import { getStatusData } from '../utils/statusCache.js';

export default {
  mixins: [ivRecordingMixin, ivChunkUploadMixin, ivTwilioWebrtcMixin],
  name: 'Record',
  components: {
    ProfileDropdown
  },
  data() {
    return {
      // Pre-recording modal state
      currentPreRecordingModal: 1, // 1, 2, 3, 4 for the four modals, 0 for none
      quietPlaceAnswer: '',
      timeAvailableAnswer: '',
      faceVisibleAnswer: '',
      integrityAnswer: '',
      
      // Recording state
      questionsStarted: false,
      currentQuestionIndex: 0,
      isRecording: false,
      isPaused: false,
      isSpeaking: false,
      cameraReady: false,
      
      // Timer
      timeLeft: 60,
      timerInterval: null,
      
      // Waveform animation timer
      waveformInterval: null,
      
      
      // Questions (will be loaded from API)
      questions: [],
      questionsAudio: [],
      questionsLoaded: false,

      // Accommodation settings
      textQuestionsEnabled: false,
      timeMultiplier: 1.0,
      answerTime: 60,
      
      // Twilio backup recording
      twilio_room_identifier: "",
      twilio_room_sid: "",
      video_take_id: null,
      twilioJoined: false,
      twilioFailed: false,
      videoTakeNotReady: false,
      questionsLoading: false,
      questionsError: null,
      questionsNotReady: false,
      audioGenerating: false,   // true while polling `generating_audio` (Card 767/839/840 Spec 1)
      audioPollInterval: null,  // setInterval handle for pollAudioStatus(), mirrors Practice.vue's questionPollInterval
      audioPollFailureCount: 0, // consecutive network/exception failures polling audio status, reset on any successful response
      AUDIO_POLL_MAX_CONSECUTIVE_FAILURES: 5,

      // iv-record recording data
      videoRecorder: null,
      stream: null,

      // Web Audio API for mixing
      audioContext: null,
      micSource: null,
      mixedOutputStream: null,
      
      // Question timestamp tracking
      questionTimestamps: [],
      sessionStartTime: null,
      currentQuestionTimeAllotted: 0, // timeLeft at the moment the current question's timer started

      // Upload progress tracking
      originalVideoUploadProgress: 0,
      showUploadProgress: false,
      uploadComplete: false,
      uploadStalled: false,
      uploadTimeoutSeconds: 0,
      uploadTimeoutTimer: null,
      showRetryInstructions: false,
      uploadError: null,
      uploadStartTime: null,

      // Countdown state
      showCountdown: false,
      countdownValue: 5,

      // Profile dropdown
      studentName: '',
      isAssignment: false
    }
  },
  mounted() {
    setEnv(env); // needed for iv-record
    this.setupCamera();
    this.fetchStudentInfo();

    // Listen for video upload completion
    ivbus.$on("videoRecordingUploadComplete", (video_file_uid) => {
      console.log("Received videoRecordingUploadComplete event with UID:", video_file_uid);
      this.handleVideoUploadComplete(video_file_uid);
    });
  },
  
  beforeDestroy() {
    this.cleanup();
    // Remove event listener
    ivbus.$off("videoRecordingUploadComplete");
  },
  
  methods: {
    fetchStudentInfo() {
      const statusData = getStatusData();
      if (statusData) {
        if (statusData.student_name !== undefined) {
          this.studentName = statusData.student_name;
        }
        if (statusData.is_assignment !== undefined) {
          this.isAssignment = statusData.is_assignment;
        }
      }
    },

    handleModalBackdropClick() {
      // Don't allow closing by clicking backdrop - user must type "Yes"
    },
    
    // We don't need three separate functions for these - Claude just likes to duplicate code
    // let's combine these at some point
    checkQuietPlace() {
      if (this.quietPlaceAnswer.trim() === 'Yes') {
        this.currentPreRecordingModal = 2;
      } else {
        alert('You must type "Yes" exactly to continue');
        this.quietPlaceAnswer = '';
      }
    },

    checkTimeAvailable() {
      if (this.timeAvailableAnswer.trim() === 'Yes') {
        this.currentPreRecordingModal = 3;
      } else {
        alert('You must type "Yes" exactly to continue');
        this.timeAvailableAnswer = '';
      }
    },

    checkFaceVisible() {
      if (this.faceVisibleAnswer.trim() === 'Yes') {
        this.currentPreRecordingModal = 4;
      } else {
        alert('You must type "Yes" exactly to continue');
        this.faceVisibleAnswer = '';
      }
    },

    checkIntegrity() {
      if (this.integrityAnswer.trim() === 'Yes') {
        this.integrityAnswer = '';
        this.currentPreRecordingModal = 0;
      } else {
        alert('You must type "Yes" exactly to continue');
        this.integrityAnswer = '';
      }
    },
    
    async setupCamera() {
      console.log('Attempting to setup camera...');
      try {
        this.stream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: true
        });

        if (document.getElementById('testViewfinder')) {
          if (!document.getElementById('testViewfinder').srcObject) {
            document.getElementById('testViewfinder').srcObject = this.stream;
          }
        }
        if (document.getElementById('viewfinder')) {
          document.getElementById('viewfinder').srcObject = this.stream;
          this.cameraReady = true;
          console.log('Camera stream setup successfully');

          // Setup audio mixing with Web Audio API
          this.setupAudioMixing();

          // Setup Twilio backup recording
          this.setupTwilioBackup();
          
        } else {
          console.warn('Video element not found, retrying in 1 second...');
          setTimeout(() => {
            this.setupCamera();
          }, 1000);
        }
      } catch (error) {
        console.error('Camera setup failed:', error);
        alert('Camera access is required for recording. Please allow camera permissions and refresh the page.');
      }
    },

    setupAudioMixing() {
      try {
        // Create Web Audio API context
        this.audioContext = new (window.AudioContext || window.webkitAudioContext)();

        // Create source from microphone stream
        this.micSource = this.audioContext.createMediaStreamSource(this.stream);

        // Create destination for mixed audio
        this.mixedOutputStream = this.audioContext.createMediaStreamDestination();

        // Connect microphone to destination (questions will be added dynamically)
        this.micSource.connect(this.mixedOutputStream);

        console.log('Audio mixing setup completed');
      } catch (error) {
        console.error('Audio mixing setup failed:', error);
        // Fall back to original stream if mixing fails
      }
    },

    async setupTwilioBackup() {
      try {
        console.log('Setting up Twilio backup recording...');
        const vparam = sessionStorage.getItem('vparam');
        const params = vparam ? { vparam } : {};
        const response = await apiClient.get('/api/twiliovideo/get_viva_room_token/', { params });
        const disableTwilio = sessionStorage.getItem('cypress-test-disable-twilio-8jge8j435gherew9023') === "true";

        if (response.data.status === 200) {
          this.twilio_room_identifier = response.data.video_room;
          this.twilio_room_sid = response.data.room_sid;
          this.video_take_id = response.data.video_take_id;

          console.log(`Joining Twilio backup room: ${this.twilio_room_identifier}`);

          // If testing, prevent from joining Twilio to avoid throttling.
          if (!disableTwilio) {
            // Join Twilio room with camera stream
            this.twilioJoin(response.data, this.stream);
            console.log('Successfully joined Twilio backup room');
          } else {
            console.log('Twilio disabled for testing');
          }

          this.twilioJoined = true;
        } else {
          console.warn('Twilio backup setup failed:', response.data.message);

          // Check if the error is because VivaVideoTake doesn't exist yet (PDF still processing)
          if (response.data.message && response.data.message.includes('No video take found')) {
            this.videoTakeNotReady = true;
          } else {
            this.twilioFailed = true;
          }
        }
      } catch (error) {
        console.error('Twilio backup setup error:', error);
        this.twilioFailed = true;
      }
    },
    
    async loadQuestionsFromAPI() {
      this.questionsLoading = true;
      this.questionsError = null;

      try {
        const vparam = sessionStorage.getItem('vparam');
        const params = vparam ? { vparam } : {};
        const response = await apiClient.get('/api/viva/questions/', { params });

        if (response.data.status === 'success') {
          this.questions = response.data.questions;
          this.questionsAudio = response.data.questions_audio;
          this.questionsLoaded = true;

          // Capture accommodation settings
          this.textQuestionsEnabled = response.data.text_questions_enabled || false;
          this.timeMultiplier = response.data.time_multiplier || 1.0;
          this.answerTime = response.data.answer_time || 60;

          console.log(`Loaded ${this.questions.length} AI-generated questions with audio`);
          console.log(`Accommodations: Text Questions=${this.textQuestionsEnabled}, Time Multiplier=${this.timeMultiplier}x`);
        } else if (response.data.status === 'generating_audio') {
          // Questions exist but their audio is still being generated in the background
          // (Card 767/839/840 Spec 1, ensure_viva_audio_ready()). Hand off to the narrow
          // poller below instead of retrying this function directly - see the no-retry-into-
          // the-main-action-function rule at handleQuestionsNotReady() just below.
          this.startAudioPolling(response.data.retry_delay_seconds);
          return;
        } else if (response.data.error_code === 'questions_not_ready') {
          this.handleQuestionsNotReady(response.data);
          return;
        } else if (response.data.status === 'failed') {
          // Audio generation failed server-side (views.py's "failed" branch, HTTP 200) --
          // surface the real backend message directly, same as pollAudioStatus() does,
          // rather than falling through to the generic throw/catch below, which discards
          // it (a plain Error has no .response, so the catch's network-error fallback wins).
          this.questionsError = response.data.message;
          return;
        } else {
          throw new Error(response.data.message || 'Failed to load questions');
        }
        
      } catch (error) {
        console.error('Error loading questions:', error);
        
        // Handle specific error responses
        if (error.response && error.response.data) {
          const errorData = error.response.data;
          
          if (errorData.error_code === 'questions_not_ready') {
            this.handleQuestionsNotReady(errorData);
            return;
          }
          
          this.questionsError = errorData.message || 'Unable to load questions. Please check your connection and try again.';
        } else {
          this.questionsError = 'Unable to load questions. Please check your connection and try again.';
        }
      } finally {
        this.questionsLoading = false;
      }
    },

    handleQuestionsNotReady(errorData) {
      this.questionsNotReady = true;
      this.questionsError = errorData.message;
      
      // Automatically retrying is dangerous from the front end - user could be surprised when their exam starts with no warning,
      // or could access the questions with time to prepare if the exam doesn't auto-start.
      // See `.agents/skills/frontend-api-integration.md` before ever re-enabling a retry here -
      // it must poll a narrow status check, not loadQuestionsFromAPI() directly (see below).

      // console.log('Questions not ready yet, will retry automatically');

      // // Auto-retry after suggested delay
      // const retryDelay = (errorData.retry_delay_seconds || 120) * 1000;
      // setTimeout(() => {
      //   if (this.questionsNotReady) {
      //     console.log('Auto-retrying to load questions...');
      //     this.loadQuestionsFromAPI();
      //   }
      // }, retryDelay);
    },

    // Card 767/839/840 Spec 1: narrow polling for the 'generating_audio' status returned by
    // /api/viva/questions/ once questions exist but their audio is still being produced by the
    // background job (ensure_viva_audio_ready() in shield/viva/views.py). This is safe to
    // auto-retry where handleQuestionsNotReady()'s case above is not: startFirstQuestion() only
    // reaches loadQuestionsFromAPI() (and therefore this polling) after the student has already
    // clicked START QUESTION 1, so there is no risk of the exam silently starting on its own.
    //
    // Per `.agents/skills/frontend-api-integration.md`'s no-retry-into-the-main-action-function
    // rule: pollAudioStatus() below calls the same /api/viva/questions/ endpoint directly rather
    // than re-calling loadQuestionsFromAPI() or startFirstQuestion(). It only ever flips a status
    // flag on a 'generating_audio' response, and only populates real question/audio data on a
    // genuine 'success' response - never anything with the exam-starting side effects that live
    // in startFirstQuestion() and beyond.
    // 
    // IMPORTANT: This is a fallback option for rare cases where audio generation succeeds and the
    // file is later corrupted on the disk. We do NOT want students to encounter this; Practice.vue 
    // should gate on the audio being ready before they can start the exam. This is only here to handle
    // rare edge cases where the student gets past that gate and the audio is still not ready. 
    startAudioPolling(retryDelaySeconds) {
      this.audioGenerating = true;
      this.audioPollFailureCount = 0;
      const intervalMs = (retryDelaySeconds || 5) * 1000;
      this.audioPollInterval = setInterval(() => {
        this.pollAudioStatus();
      }, intervalMs);
    },

    async pollAudioStatus() {
      try {
        const vparam = sessionStorage.getItem('vparam');
        const params = vparam ? { vparam } : {};
        const response = await apiClient.get('/api/viva/questions/', { params });
        this.audioPollFailureCount = 0;

        if (response.data.status === 'success') {
          this.stopAudioPolling();
          this.questions = response.data.questions;
          this.questionsAudio = response.data.questions_audio;
          this.questionsLoaded = true;
          this.textQuestionsEnabled = response.data.text_questions_enabled || false;
          this.timeMultiplier = response.data.time_multiplier || 1.0;
        } else if (response.data.status === 'failed') {
          this.stopAudioPolling();
          this.questionsError = response.data.message;
        }
        // else: still 'generating_audio' (or some other transient shape) - do nothing and
        // let the interval fire again. No cap on this branch, matching the Practice.vue
        // question_readiness poll this mirrors: the backend's own staleness check
        // (AUDIO_JOB_STALE_MINUTES) is what eventually turns this into 'failed'. That
        // safety net only fires once a request actually reaches the backend, though --
        // see the catch block below for the case where it never does.
      } catch (error) {
        console.error('Error polling audio status:', error);
        // A transient network blip shouldn't kill the poll - but a *persistent* one
        // (offline, server unreachable) would otherwise retry forever with the student
        // stuck on "Preparing audio..." and no error ever shown, since the backend's own
        // staleness check never gets a chance to run if requests aren't reaching it at
        // all. Cap consecutive failures and surface an error once the cap is hit.
        this.audioPollFailureCount++;
        if (this.audioPollFailureCount >= this.AUDIO_POLL_MAX_CONSECUTIVE_FAILURES) {
          this.stopAudioPolling();
          this.questionsError = 'Unable to reach the server. Please check your connection and refresh the page.';
        }
      }
    },

    stopAudioPolling() {
      this.audioGenerating = false;
      this.audioPollFailureCount = 0;
      if (this.audioPollInterval) {
        clearInterval(this.audioPollInterval);
        this.audioPollInterval = null;
      }
    },

    async startFirstQuestion() {
      // Only load questions when user clicks "BEGIN QUESTION 1"
      if (!this.questionsLoaded) {
        await this.loadQuestionsFromAPI();
      }

      if (this.questionsError || this.questions.length === 0) {
        // Don't start if there was an error loading questions
        return;
      }

      // Show countdown after questions are loaded
      await this.startCountdown();

      this.sessionStartTime = Date.now(); // Track when recording session starts
      this.questionsStarted = true;
      await this.startContinuousRecording();
      this.speakQuestion();
      this.startTimer();
    },
    
    
    async startContinuousRecording() {
      if (this.stream && this.mixedOutputStream) {
        // Create mixed stream with video from camera and mixed audio
        const videoTracks = this.stream.getVideoTracks();
        const mixedAudioTracks = this.mixedOutputStream.stream.getAudioTracks();
        const recordingStream = new MediaStream([...videoTracks, ...mixedAudioTracks]);

        // Initialize iv-record with mixed stream
        this.videoRecorder = new ivRecorder("video", "VivaExam", this);
        await this.videoRecorder.start(recordingStream);
        this.isRecording = true;

        console.log('Continuous recording started with mixed audio stream');
      } else {
        console.error('Mixed audio stream not available, falling back to original stream');
        this.videoRecorder = new ivRecorder("video", "VivaExam", this);
        await this.videoRecorder.start(this.stream);
        this.isRecording = true;
      }
    },
    
    async speakQuestion() {
      try {
        // Record timestamp when question starts being asked (only on first play)
        if (this.questions[this.currentQuestionIndex]) {
          const questionId = this.questions[this.currentQuestionIndex].id;

          // Only record timestamp on first play
          if (!this.questionTimestamps.some(ts => ts.question_id === questionId)) {
            const currentTime = Date.now();
            const relativeTimeSeconds = Math.floor((currentTime - this.sessionStartTime) / 1000);
            const timestamp = this.formatTimestamp(relativeTimeSeconds);

            this.questionTimestamps.push({
              question_id: questionId,
              timestamp: timestamp,
              response_duration_seconds: null,
              repeat_count: 0
            });

            console.log(`Question ${this.currentQuestionIndex + 1} timestamp: ${timestamp}`);
          }
        }

        console.log(`Playing question ${this.currentQuestionIndex + 1} audio`);

        this.isSpeaking = true;
        this.pauseTimer();
        this.startWaveformAnimation();

        // Play the audio through Web Audio API and mix with microphone
        await this.playQuestionAudio(this.questionsAudio[this.currentQuestionIndex]);

        this.isSpeaking = false;
        this.resumeTimer();
        this.stopWaveformAnimation();

      } catch (error) {
        console.error('Error playing question audio:', error);
        this.isSpeaking = false;
        this.resumeTimer();
        this.stopWaveformAnimation();
      }
    },

    async playQuestionAudio(base64Audio) {
      return new Promise((resolve, reject) => {
        try {
          // Decode base64 to array buffer
          const binaryString = atob(base64Audio);
          const arrayBuffer = new ArrayBuffer(binaryString.length);
          const uint8Array = new Uint8Array(arrayBuffer);

          for (let i = 0; i < binaryString.length; i++) {
            uint8Array[i] = binaryString.charCodeAt(i);
          }

          // Decode audio data
          this.audioContext.decodeAudioData(arrayBuffer)
            .then(audioBuffer => {
              // Create buffer source
              const source = this.audioContext.createBufferSource();
              source.buffer = audioBuffer;

              // Connect to mixed output stream (so it gets recorded)
              source.connect(this.mixedOutputStream);

              // Also connect to speakers for the student to hear
              source.connect(this.audioContext.destination);

              source.onended = () => {
                console.log('Question audio finished playing');
                resolve();
              };

              source.start(0);
            })
            .catch(error => {
              console.error('Error decoding audio:', error);
              reject(error);
            });

        } catch (error) {
          console.error('Error processing base64 audio:', error);
          reject(error);
        }
      });
    },

    generateBeep(frequency = 800, duration = 200) {
      if (!this.audioContext) return;

      const oscillator = this.audioContext.createOscillator();
      const gainNode = this.audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(this.audioContext.destination);

      oscillator.frequency.setValueAtTime(frequency, this.audioContext.currentTime);
      oscillator.type = 'sine';

      gainNode.gain.setValueAtTime(0.3, this.audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + duration / 1000);

      oscillator.start(this.audioContext.currentTime);
      oscillator.stop(this.audioContext.currentTime + duration / 1000);
    },

    async startCountdown() {
      return new Promise((resolve) => {
        this.showCountdown = true;
        this.countdownValue = 5;

        this.generateBeep(); // Initial beep
        const countdownInterval = setInterval(() => {
          this.generateBeep(); // Play beep
          this.countdownValue--;

          if (this.countdownValue === 0) {
            clearInterval(countdownInterval);
            this.showCountdown = false;
            resolve();
          }
        }, 1000);
      });
    },
    
    repeatQuestion() {
      const questionId = this.questions[this.currentQuestionIndex] && this.questions[this.currentQuestionIndex].id;
      const entry = this.questionTimestamps.find(ts => ts.question_id === questionId);
      if (entry) {
        entry.repeat_count++;
      }
      this.speakQuestion();
    },

    startTimer() {
      // Apply time multiplier for accommodations (e.g., 1.5x = 90 seconds)
      this.timeLeft = Math.floor(this.answerTime * this.timeMultiplier);
      this.currentQuestionTimeAllotted = this.timeLeft;
      this.timerInterval = setInterval(() => {
        if (!this.isPaused && this.timeLeft > 0) {
          this.timeLeft--;
        }
        if (this.timeLeft === 0) {
          this.timeUp();
        }
      }, 1000);
    },
    
    pauseTimer() {
      this.isPaused = true;
    },
    
    resumeTimer() {
      this.isPaused = false;
    },
    
    timeUp() {
      this.concludeRecording();
    },
    
    concludeRecording() {
      // Re-entrancy guard: concludeRecording() is reachable both from the timer's
      // automatic timeUp() and a manual button click, and the buttons aren't disabled
      // in the last moment before timeout. If a stray/queued trigger fires again right
      // after a first call already ran, isSpeaking (advancing to the next question) or
      // showUploadProgress (finishing the last question) will already be true by then -
      // both are set synchronously before speakQuestion()/allQuestionsComplete() hit
      // their first await, so this reliably blocks the duplicate without needing a
      // dedicated flag. Without this, a duplicate call double-increments
      // currentQuestionIndex (skipping a question) and writes response_duration_seconds
      // against the wrong question.
      if (this.isSpeaking || this.showUploadProgress) {
        return;
      }

      clearInterval(this.timerInterval);

      // Record how long the student spent answering the current question
      // (timeLeft only counts down while the timer is active/unpaused, i.e. not
      // while question audio is speaking, so this reflects actual response time)
      const questionId = this.questions[this.currentQuestionIndex] && this.questions[this.currentQuestionIndex].id;
      const entry = this.questionTimestamps.find(ts => ts.question_id === questionId);
      if (entry) {
        entry.response_duration_seconds = Math.max(0, this.currentQuestionTimeAllotted - this.timeLeft);
      }

      // Move to next question or complete
      if (this.currentQuestionIndex < this.questions.length - 1) {
        this.currentQuestionIndex++;
        // Time will be set by startTimer() with multiplier applied
        // Continue recording - just move to next question
        this.speakQuestion();
        this.startTimer();
      } else {
        // All questions complete - stop and download
        this.allQuestionsComplete();
      }
    },
    
    async allQuestionsComplete() {
      console.log('All questions completed');
      this.showUploadProgress = true;
      this.originalVideoUploadProgress = 0;
      this.uploadError = null;
      this.uploadStalled = false;
      this.showRetryInstructions = false;
      this.uploadStartTime = Date.now();

      if (this.videoRecorder && this.isRecording) {
        try {
          await this.videoRecorder.stop();
          this.isRecording = false;

          // Start upload timeout monitoring
          this.startUploadTimeoutMonitoring();

          // originalVideoUploadProgress will be automatically updated during upload
          await this.videoRecorder.upload("videoRecordingUploadComplete");

          console.log('Video upload initiated - waiting for completion...');

        } catch (error) {
          console.error('Error stopping/uploading recording:', error);
          this.handleUploadError(error);
          return;
        }
      }
    },

    startUploadTimeoutMonitoring() {
      // Clear any existing timer
      if (this.uploadTimeoutTimer) {
        clearInterval(this.uploadTimeoutTimer);
      }

      let lastProgress = this.originalVideoUploadProgress;
      let stallCount = 0;
      const STALL_THRESHOLD_SECONDS = 60; // Require 60 seconds of no progress
      const INITIAL_GRACE_PERIOD = 90; // Give 90 seconds for initial upload to start from 0%
      const PROGRESS_TOLERANCE = 0.5; // Consider progress if it increases by at least 0.5%

      this.uploadTimeoutTimer = setInterval(() => {
        // Check if progress has increased meaningfully
        const progressDifference = this.originalVideoUploadProgress - lastProgress;

        if (progressDifference >= PROGRESS_TOLERANCE) {
          // Meaningful progress detected
          lastProgress = this.originalVideoUploadProgress;
          stallCount = 0;
          this.uploadStalled = false;
          this.showRetryInstructions = false;
          console.log(`Upload progress: ${this.originalVideoUploadProgress}%`);
        } else if (Math.abs(progressDifference) < PROGRESS_TOLERANCE) {
          // No meaningful progress (could be slightly negative due to rounding)
          stallCount++;

          // Use longer threshold if we're still at 0% (initial upload startup)
          const threshold = (this.originalVideoUploadProgress === 0) ? INITIAL_GRACE_PERIOD : STALL_THRESHOLD_SECONDS;

          // Consider stalled after threshold seconds of no meaningful progress
          if (stallCount >= threshold && !this.uploadComplete) {
            console.log(`Upload appears to be stalled at ${this.originalVideoUploadProgress}% for ${stallCount} seconds`);
            this.uploadStalled = true;
            this.showRetryInstructions = true;
          }
        }

        // Update time elapsed
        if (this.uploadStartTime) {
          this.uploadTimeoutSeconds = Math.floor((Date.now() - this.uploadStartTime) / 1000);
        }

      }, 1000); // Check every second
    },

    stopUploadTimeoutMonitoring() {
      if (this.uploadTimeoutTimer) {
        clearInterval(this.uploadTimeoutTimer);
        this.uploadTimeoutTimer = null;
      }
    },

    handleUploadError(error) {
      console.error('Upload error:', error);
      this.uploadError = error.message || 'Upload failed';
      this.stopUploadTimeoutMonitoring();
    },

    retryUpload() {
      // Reset upload state and try again
      this.uploadError = null;
      this.uploadStalled = false;
      this.showRetryInstructions = false;
      this.originalVideoUploadProgress = 0;
      this.uploadStartTime = Date.now();
      
      // Restart the upload process
      if (this.videoRecorder) {
        console.log('Retrying video upload...');
        this.startUploadTimeoutMonitoring();
        this.videoRecorder.upload("videoRecordingUploadComplete").catch(error => {
          this.handleUploadError(error);
        });
      }
    },
    
    async handleVideoUploadComplete(video_file_uid) {
      console.log('Video upload completed with UID:', video_file_uid);
      
      // Stop upload timeout monitoring since upload is complete
      this.stopUploadTimeoutMonitoring();
      
      try {
        // Use retry mechanism instead of fixed delay
        const completeRecordingOperation = async () => {
          const vparam = sessionStorage.getItem('vparam');
          const payload = {
            video_file_uid: video_file_uid,
            question_timestamps: this.questionTimestamps
          };

          // Add vparam if coursework student
          if (vparam) {
            payload.vparam = vparam;
          }

          return await apiClient.post('/api/viva/complete_recording/', payload);
        };
        
        const response = await retryCompleteRecording(completeRecordingOperation, video_file_uid);
        
        if (response.data.status === 'success') {
          console.log('Recording completion processed successfully');
          this.uploadComplete = true;

          // Navigate to success page after successful processing
          setTimeout(() => {
            this.$router.push('/success');
          }, 1000);
          
        } else {
          throw new Error(response.data.message || 'Failed to process recording completion');
        }
        
      } catch (error) {
        console.error('Error calling complete_recording endpoint:', error);
        this.handleUploadError(new Error('Error processing recording completion. Please contact support.'));
      }
    },
    
    formatTimestamp(seconds) {
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = seconds % 60;
      return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
    },

    formatUploadTime(seconds) {
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = seconds % 60;
      return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
    },
    
    startWaveformAnimation() {
      // Force re-render every 100ms during speech for smooth animation
      this.waveformInterval = setInterval(() => {
        this.$forceUpdate();
      }, 100);
    },
    
    stopWaveformAnimation() {
      if (this.waveformInterval) {
        clearInterval(this.waveformInterval);
        this.waveformInterval = null;
      }
    },
    
    getBarHeight(index) {
      console.log("getBarHeight called");
      // Simple animated waveform bars
      if (!this.isSpeaking) return '4px';
      const base = 4;
      const variation = Math.sin((Date.now() / 100) + index) * 15 + 15;
      return Math.max(base, variation) + 'px';
    },
    
    cleanup() {
      if (this.timerInterval) {
        clearInterval(this.timerInterval);
      }
      if (this.waveformInterval) {
        clearInterval(this.waveformInterval);
      }
      // Card 767/839/840 Spec 1: stop audio-status polling on unmount/navigation, same
      // reasoning as the other intervals cleared here.
      this.stopAudioPolling();
      if (this.audioContext) {
        this.audioContext.close();
      }
      if (this.stream) {
        this.stream.getTracks().forEach(track => track.stop());
      }
      if (this.videoRecorder && this.isRecording) {
        try {
          this.videoRecorder.stop();
        } catch (error) {
          console.error('Error stopping recorder during cleanup:', error);
        }
      }

      // Stop upload timeout monitoring
      this.stopUploadTimeoutMonitoring();

      // Leave Twilio backup room
      if (this.twilioJoined) {
        try {
          this.twilioLeave();
          console.log('Left Twilio backup room');
        } catch (error) {
          console.error('Error leaving Twilio room:', error);
        }
      }
    }
  }
}
</script>

<style scoped>
@import '../styles/viva-classes.css';

.record-page {
  min-height: 100vh;
  background-color: var(--viva-cream);
}

/* Record page uses shared classes from viva-classes.css */

/* Start Button */
.start-section {
  text-align: center;
}

.start-question-btn {
  font-size: 20px;
  font-weight: 600;
  padding: 25px 50px;
  min-width: 320px;
  position: relative;
}

.start-arrow {
  margin-top: 10px;
  font-size: 24px;
  color: var(--viva-white);
}

/* Error Styling */
.questions-error {
  margin-top: 15px;
  padding: 15px;
  background-color: #fff3cd;
  border: 1px solid #ffeaa7;
  border-radius: 6px;
  color: #856404;
  font-size: 14px;
  max-width: 400px;
  margin-left: auto;
  margin-right: auto;
  text-align: center;
}

.questions-error i {
  margin-right: 5px;
}

.start-question-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

/* Modal Input Styling */
.input-section {
  margin: 20px 0;
}

.modal-input {
  width: 100%;
  max-width: 300px;
  margin: 0 auto;
  padding: 12px;
  font-size: 16px;
  border: 2px solid var(--viva-green);
  border-radius: 6px;
  text-align: center;
}

.modal-input:focus {
  outline: none;
  border-color: var(--viva-orange);
  box-shadow: 0 0 5px rgba(245, 176, 65, 0.3);
}

/* Countdown Overlay */
.countdown-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
}

.countdown-content {
  background: var(--viva-white);
  border-radius: 20px;
  padding: 60px 80px;
  text-align: center;
  border: 4px solid var(--viva-green);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
}

.countdown-content h2 {
  font-size: 28px;
  font-weight: 700;
  color: var(--viva-dark);
  margin: 0 0 30px 0;
}

.countdown-number {
  font-size: 120px;
  font-weight: 900;
  color: var(--viva-orange);
  margin: 20px 0;
  line-height: 1;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.1);
  /* animation: countdown-pulse 1s ease-in-out infinite; */
}

.countdown-content p {
  font-size: 18px;
  color: var(--viva-dark);
  margin: 20px 0 0 0;
  font-weight: 500;
}

/* @keyframes countdown-pulse {
  0% { transform: scale(1); }
  50% { transform: scale(1.1); }
  100% { transform: scale(1); }
} */

/* Upload Progress Overlay */
.upload-progress-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.upload-progress-content {
  background: var(--viva-white);
  border-radius: 12px;
  padding: 40px;
  max-width: 500px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
  text-align: center;
  border: 3px solid var(--viva-green);
}

.upload-progress-content h3 {
  font-size: 24px;
  font-weight: 600;
  color: var(--viva-dark);
  margin-bottom: 30px;
}

.upload-progress {
  margin-bottom: 20px;
}

.progress {
  height: 20px;
  background-color: #f5f5f5;
  border-radius: 10px;
  overflow: hidden;
  margin-bottom: 15px;
}

.progress-bar {
  height: 100%;
  background-color: var(--viva-green);
  border-radius: 10px;
  transition: width 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
}

.progress-bar.active {
  animation: progress-bar-stripes 2s linear infinite;
}

@keyframes progress-bar-stripes {
  from { background-position: 40px 0; }
  to { background-position: 0 0; }
}

.processing-phase {
  padding: 20px;
}


.upload-complete {
  padding: 20px;
}

.success-icon {
  font-size: 64px;
  color: var(--viva-green);
  margin-bottom: 20px;
}

.upload-progress-content p {
  font-size: 16px;
  color: var(--viva-dark);
  margin: 0;
}

/* Enhanced Upload Progress Styles */
.reassurance-header {
  margin-bottom: 25px;
  padding: 20px;
  background: linear-gradient(135deg, #f5eee8 0%, #f8f9fa 100%);
  border-radius: 10px;
  border: 2px solid var(--viva-orange);
}

.backup-shield-icon {
  font-size: 48px;
  color: var(--viva-orange);
  margin-bottom: 15px;
  display: block;
}

.reassurance-header h3 {
  font-size: 22px;
  color: var(--viva-orange);
  margin: 0 0 10px 0;
  font-weight: 700;
}

.backup-message {
  font-size: 14px;
  color: var(--viva-dark);
  margin: 0;
  line-height: 1.4;
}

/* Upload Error State */
.upload-error-state {
  text-align: center;
}

.error-icon {
  font-size: 48px;
  color: #dc3545;
  margin-bottom: 15px;
  display: block;
}

.upload-error-state h4 {
  color: var(--viva-dark);
  font-size: 20px;
  margin: 0 0 15px 0;
  font-weight: 600;
}

.error-message {
  font-size: 14px;
  color: #6c757d;
  margin: 0 0 20px 0;
  padding: 10px;
  background: #f8f9fa;
  border-radius: 6px;
  font-family: monospace;
}

.error-reassurance {
  background: #d1edff;
  border: 1px solid #b3d9ff;
  border-radius: 8px;
  padding: 20px;
  margin: 20px 0;
}

.error-reassurance p {
  margin: 0 0 10px 0;
  font-size: 15px;
  line-height: 1.4;
}

.error-reassurance p:last-child {
  margin-bottom: 0;
}

.error-actions {
  display: flex;
  gap: 15px;
  justify-content: center;
  margin-top: 25px;
  flex-wrap: wrap;
}

.error-actions button {
  min-width: 180px;
}

/* Backup Status Indicator */
.backup-status {
  background: #d4edda;
  border: 1px solid #c3e6cb;
  border-radius: 6px;
  padding: 10px 15px;
  margin-bottom: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.backup-check {
  color: var(--viva-green);
  font-size: 16px;
}

.backup-status span {
  font-size: 14px;
  font-weight: 600;
  color: var(--viva-dark);
}

/* Progress Bar States */
.progress-stalled {
  background-color: #ffc107 !important;
}

.upload-status-text {
  margin: 10px 0;
}

.upload-stalled-text {
  color: #856404;
  font-weight: 500;
  text-align: center;
}

.upload-time {
  font-size: 12px;
  color: var(--viva-grey);
  margin: 10px 0 0 0;
  text-align: center;
}

/* Stalled Instructions */
.stalled-instructions {
  background: #fff3cd;
  border: 1px solid #ffeaa7;
  border-radius: 8px;
  padding: 20px;
  margin-top: 20px;
  text-align: left;
}

.stalled-help {
  display: flex;
  gap: 15px;
  align-items: flex-start;
  margin-bottom: 20px;
}

.stalled-help i {
  color: #856404;
  font-size: 20px;
  margin-top: 3px;
  flex-shrink: 0;
}

.stalled-content h5 {
  font-size: 16px;
  font-weight: 600;
  color: var(--viva-dark);
  margin: 0 0 10px 0;
}

.stalled-content p {
  font-size: 14px;
  color: var(--viva-dark);
  margin: 0 0 10px 0;
}

.stalled-content ul {
  font-size: 13px;
  color: var(--viva-dark);
  margin: 0;
  padding-left: 18px;
  line-height: 1.4;
}

.stalled-content li {
  margin-bottom: 4px;
}

.retry-btn {
  width: 100%;
  text-align: center;
}

/* Secondary Button Style */
.viva-btn-secondary {
  background-color: #6c757d;
  border-color: #6c757d;
  color: white;
}

.viva-btn-secondary:hover {
  background-color: #5a6268;
  border-color: #545b62;
}

.viva-btn-secondary:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

/* Question Text Section (for deaf/hard-of-hearing accommodation) */
.question-text-section {
  margin: 0;
}

.question-text-box {
  background: linear-gradient(135deg, #f5b041 0%, #f39c12 100%);
  border-radius: 12px;
  padding: 25px 30px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  border: 3px solid #e67e22;
}

.question-text-content {
  font-size: 20px;
  font-weight: 600;
  color: var(--viva-white);
  margin: 0 0 15px 0;
  line-height: 1.5;
  text-align: center;
}

.question-text-note {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.9);
  margin: 0;
  text-align: center;
  font-style: italic;
}

.question-text-note i {
  margin-right: 5px;
}

.question-text-ready {
  color: #d4edda;
  font-weight: 700;
}

/* Responsive Design */
@media (max-width: 1024px) {
  .recording-layout {
    gap: 40px;
    max-width: 1000px;
  }
  
  .controls-panel {
    max-width: 450px;
  }
  
  .video-panel {
    max-width: 550px;
  }
}

@media (max-width: 768px) {
  .recording-interface {
    min-height: calc(100vh - 100px);
    align-items: flex-start;
    padding-top: 40px;
  }
  
  .recording-layout {
    flex-direction: column;
    gap: 30px;
    max-width: 100%;
    padding: 0 20px;
  }
  
  .controls-panel,
  .video-panel {
    max-width: 100%;
  }
  
  .start-question-btn {
    min-width: 280px;
    font-size: 18px;
    padding: 20px 40px;
  }
  
  .timer-value {
    font-size: 36px;
  }
  
  .control-btn {
    font-size: 13px;
    padding: 12px 16px;
  }
  
  .video-container {
    aspect-ratio: 16/9;
  }
}
</style>
